import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingUp, Gem,
  Shield, Target, Bell, Zap, Coins,
  ChevronRight, Sparkles, Activity, AlertTriangle, Star
} from 'lucide-react';

// FAQ for SEO
const faqData = [
  {
    question: "Altcoin sinyali nedir?",
    answer: "Altcoin sinyali, Bitcoin disindaki kripto paralar (ETH, SOL, XRP, ADA vb.) icin verilen trading sinyalleridir. Altcoinler genellikle Bitcoin'den daha volatil oldugu icin daha yuksek getiri potansiyeli tasir ancak risk de yuksektir."
  },
  {
    question: "En iyi altcoinler hangileri?",
    answer: "Kaliteli sinyal platformlari yuksek hacimli ve likit altcoinleri tarar: ETH, SOL, XRP, ADA, AVAX, LINK, DOT, MATIC, UNI, AAVE, NEAR, APT, FET, INJ. Dusuk hacimli, pump-dump riski tasiyan coinlerden kacinilir."
  },
  {
    question: "Altcoin sinyalleri ne kadar riskli?",
    answer: "Altcoinler Bitcoin'den daha volatildir. Bu yuzden hem kar hem zarar potansiyeli daha yuksektir. Her sinyalde stop-loss seviyesi belirlenmeli ve risk yonetimi uygulanmalidir. Asla kaybetmeyi goze alamayacaginiz parayla islem yapmayin."
  },
  {
    question: "Gunde kac altcoin sinyali uretilir?",
    answer: "Sinyal sayisi piyasa kosullarina baglidir. Ortalama gunde 2-5 sinyal uretilir. Kalite kantiteden once gelir ve sadece yuksek skorlu setuplar sinyal olarak yayinlanir."
  },
  {
    question: "Hangi borsalarda altcoin trade yapilabilir?",
    answer: "Altcoin sinyalleri tum buyuk borsalarda kullanilabilir: Binance, Bybit, OKX, KuCoin, Kraken ve digerlerinde islem yapabilirsiniz. Kaliteli sinyal platformlari borsaniza baglanmaz ve API anahtari istemez."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

// Popüler altcoinler
const popularAltcoins = [
  { symbol: 'ETH', name: 'Ethereum', color: 'from-blue-500 to-purple-500' },
  { symbol: 'SOL', name: 'Solana', color: 'from-purple-500 to-pink-500' },
  { symbol: 'XRP', name: 'Ripple', color: 'from-slate-400 to-slate-600' },
  { symbol: 'ADA', name: 'Cardano', color: 'from-blue-400 to-blue-600' },
  { symbol: 'AVAX', name: 'Avalanche', color: 'from-red-500 to-red-700' },
  { symbol: 'LINK', name: 'Chainlink', color: 'from-blue-500 to-blue-700' },
  { symbol: 'DOT', name: 'Polkadot', color: 'from-pink-500 to-pink-700' },
  { symbol: 'MATIC', name: 'Polygon', color: 'from-purple-500 to-purple-700' },
];

const AltcoinSinyalleriPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Altcoin Sinyalleri – ETH, SOL, XRP ve Daha Fazlası | Borsamex"
        description="Altcoin sinyalleri ile ETH, SOL, XRP, ADA ve diğer kripto paralarda trading fırsatlarını yakalayın. Risk yönetimli profesyonel sinyaller. Ücretsiz deneyin."
        keywords="altcoin sinyalleri, ethereum sinyali, solana sinyali, xrp sinyali, ada sinyali, altcoin analizi, kripto sinyalleri"
        canonical="https://borsamex.com/altcoin-sinyalleri"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-purple-500/10 text-purple-400 border-purple-500/20">
              <Gem className="h-4 w-4 mr-2" />
              ETH, SOL, XRP ve Daha Fazlası
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              Altcoin Sinyalleri<br />
              <span className="text-brand-green">Bitcoin Ötesinde Fırsatlar</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              <strong className="text-white">Altcoin sinyalleri</strong> ile ETH, SOL, XRP ve diğer popüler coinlerde 
              trading fırsatlarını yakalayın. Her sinyal <strong className="text-brand-green">risk yönetimli</strong> ve 
              <strong className="text-brand-green"> detaylı analiz</strong> içerir.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                  Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/signals">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  Sinyalleri Gör
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Altcoin Nedir */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Altcoin Nedir?
              </h2>
              <p className="text-slate-300 mb-4">
                <strong className="text-white">Altcoin</strong> (alternative coin), Bitcoin dışındaki tüm kripto paraların genel adıdır. 
                Ethereum, Solana, XRP gibi büyük projelerden küçük projelere kadar binlerce altcoin bulunur.
              </p>
              <p className="text-slate-300 mb-6">
                Altcoinler genellikle Bitcoin'den daha <strong className="text-white">volatil</strong>dir. 
                Bu da daha yüksek getiri potansiyeli anlamına gelir - ancak risk de aynı oranda yüksektir.
              </p>
              
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
                  <div>
                    <h4 className="text-amber-400 font-bold text-sm mb-1">Risk Uyarısı</h4>
                    <p className="text-slate-400 text-sm">
                      Altcoinler yüksek volatiliteye sahiptir. Her zaman stop-loss kullanın ve 
                      kaybetmeyi göze alamayacağınız parayla işlem yapmayın.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Popüler Altcoinler</h3>
              <div className="grid grid-cols-2 gap-3">
                {popularAltcoins.map(coin => (
                  <div key={coin.symbol} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${coin.color} flex items-center justify-center mb-2`}>
                      <span className="text-white font-bold text-sm">{coin.symbol.slice(0,2)}</span>
                    </div>
                    <div className="font-bold text-white">{coin.symbol}</div>
                    <div className="text-slate-400 text-sm">{coin.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Neden Altcoin */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Neden Altcoin Trade Edilir?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Yüksek Getiri Potansiyeli</h3>
                <p className="text-slate-400 text-sm">
                  Altcoinler Bitcoin'den daha yüksek yüzdelik hareketler yapabilir. 
                  Doğru trade ile daha yüksek getiri mümkün.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <Coins className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Çeşitlilik</h3>
                <p className="text-slate-400 text-sm">
                  Farklı sektörler, farklı use-case'ler. DeFi, gaming, AI, layer-2... 
                  Portföyünüzü çeşitlendirebilirsiniz.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                  <Activity className="h-6 w-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Daha Fazla Fırsat</h3>
                <p className="text-slate-400 text-sm">
                  Bitcoin sideways giderken bile altcoinler hareket edebilir. 
                  Daha fazla trading fırsatı demek.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Borsamex Altcoin Sinyalleri */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-4 py-2 bg-brand-green/10 text-brand-green border-brand-green/20">
              <Sparkles className="h-4 w-4 mr-2" />
              Borsamex Farkı
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Altcoin Sinyallerinde Kalite Standartları
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Sadece Likit Coinler</h3>
                  <p className="text-slate-400 text-sm">
                    Düşük hacimli, pump-dump riski taşıyan coinlerden kaçınılır. 
                    Sadece büyük borsalarda yeterli likiditeye sahip coinler taranır.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Risk/Ödül Oranı</h3>
                  <p className="text-slate-400 text-sm">
                    Her sinyal minimum 1:2 risk/ödül oranı hedefler. 
                    Stop-loss her zaman belirlenir.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Teknik Analiz Tabanlı</h3>
                  <p className="text-slate-400 text-sm">
                    Dedikodu veya hype değil, teknik göstergeler ve price action analizi.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Şeffaf Performans</h3>
                  <p className="text-slate-400 text-sm">
                    Tüm altcoin sinyalleri - kazanan ve kaybeden - açıkça gösterilir.
                    <Link to="/performans" className="text-brand-green hover:underline ml-1">Performansı gör</Link>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Anlık Bildirimler</h3>
                  <p className="text-slate-400 text-sm">
                    Telegram üzerinden anında bildirim. Fırsatları kaçırmayın.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-brand-green mt-0.5" />
                <div>
                  <h3 className="text-white font-bold mb-1">Detaylı Analiz</h3>
                  <p className="text-slate-400 text-sm">
                    Sadece "al/sat" değil, neden bu sinyalin verildiğini anlatan açıklama.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coin Bazlı Linkler */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Coin Bazlı Sinyaller
            </h2>
            <p className="text-slate-400">
              Spesifik coinler için sinyalleri inceleyin.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/btc-sinyalleri">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-colors">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">₿</div>
                  <div className="text-white font-bold">Bitcoin</div>
                  <div className="text-slate-400 text-sm">BTC Sinyalleri</div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/eth-sinyalleri">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-blue-500/50 transition-colors">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">Ξ</div>
                  <div className="text-white font-bold">Ethereum</div>
                  <div className="text-slate-400 text-sm">ETH Sinyalleri</div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/long-sinyaller">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-green-500/50 transition-colors">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <div className="text-white font-bold">Long</div>
                  <div className="text-slate-400 text-sm">Yükseliş Sinyalleri</div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/short-sinyaller">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-red-500/50 transition-colors">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-8 w-8 text-red-400 mx-auto mb-2 rotate-180" />
                  <div className="text-white font-bold">Short</div>
                  <div className="text-slate-400 text-sm">Düşüş Sinyalleri</div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Sıkça Sorulan Sorular
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">{faq.question}</h3>
                  <p className="text-slate-400 text-sm">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-b from-brand-green/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Altcoin Fırsatlarını Kaçırmayın
          </h2>
          <p className="text-slate-300 mb-8">
            Ücretsiz hesap oluşturun ve profesyonel altcoin sinyallerine hemen erişin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/en-iyi-kripto-sinyalleri">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Platform Karşılaştır
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AltcoinSinyalleriPage;
